import React, { createContext, useContext, useState, ReactNode } from 'react'

interface ApiRequest {
  url: string
  method: string
  headers: Record<string, string>
  body?: any
  timestamp: string
}

interface ApiResponse {
  status: number
  statusText: string
  headers: Record<string, string>
  data: any
  timestamp: string
}

interface ApiLog {
  id: string
  page: string
  action: string
  request: ApiRequest
  response: ApiResponse | null
  loading: boolean
  error: string | null
  timestamp: string
}

interface SelectionHistoryEntry {
  id: string
  page: string
  timestamp: string
  action: 'select' | 'unselect' | 'bulk_select' | 'bulk_unselect'
  perkTitle?: string
  perkId?: string
  campaignId?: string
  totalPerks?: number
  status: 'success' | 'error'
  response?: any
  error?: string
  curlCommand?: string
}

interface ApiLogsContextType {
  apiLogs: ApiLog[]
  selectionHistory: SelectionHistoryEntry[]
  addApiLog: (log: Omit<ApiLog, 'id' | 'timestamp'>) => string
  updateApiLog: (id: string, updates: Partial<ApiLog>) => void
  addSelectionHistory: (entry: Omit<SelectionHistoryEntry, 'id' | 'timestamp'>) => void
  clearApiLogs: () => void
  clearSelectionHistory: (page?: string) => void
  getLogsByPage: (page: string) => ApiLog[]
  getLatestLog: () => ApiLog | null
  getSelectionHistoryByPage: (page: string) => SelectionHistoryEntry[]
}

const ApiLogsContext = createContext<ApiLogsContextType | undefined>(undefined)

export const ApiLogsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [apiLogs, setApiLogs] = useState<ApiLog[]>([])
  const [selectionHistory, setSelectionHistory] = useState<SelectionHistoryEntry[]>([])

  const addApiLog = (log: Omit<ApiLog, 'id' | 'timestamp'>) => {
    const newLog: ApiLog = {
      ...log,
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    }
    setApiLogs(prev => [...prev, newLog])
    return newLog.id
  }

  const updateApiLog = (id: string, updates: Partial<ApiLog>) => {
    setApiLogs(prev => prev.map(log => 
      log.id === id ? { ...log, ...updates } : log
    ))
  }

  const addSelectionHistory = (entry: Omit<SelectionHistoryEntry, 'id' | 'timestamp'>) => {
    const newEntry: SelectionHistoryEntry = {
      ...entry,
      id: `history_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    }
    setSelectionHistory(prev => [...prev, newEntry])
  }

  const clearApiLogs = () => {
    setApiLogs([])
  }

  const clearSelectionHistory = (page?: string) => {
    if (page) {
      setSelectionHistory(prev => prev.filter(entry => entry.page !== page))
    } else {
      setSelectionHistory([])
    }
  }

  const getLogsByPage = (page: string) => {
    return apiLogs.filter(log => log.page === page)
  }

  const getLatestLog = () => {
    return apiLogs.length > 0 ? apiLogs[apiLogs.length - 1] : null
  }

  const getSelectionHistoryByPage = (page: string) => {
    return selectionHistory.filter(entry => entry.page === page)
  }

  return (
    <ApiLogsContext.Provider value={{
      apiLogs,
      selectionHistory,
      addApiLog,
      updateApiLog,
      addSelectionHistory,
      clearApiLogs,
      clearSelectionHistory,
      getLogsByPage,
      getLatestLog,
      getSelectionHistoryByPage
    }}>
      {children}
    </ApiLogsContext.Provider>
  )
}

export const useApiLogs = () => {
  const context = useContext(ApiLogsContext)
  if (context === undefined) {
    throw new Error('useApiLogs must be used within an ApiLogsProvider')
  }
  return context
}

export type { ApiLog, ApiRequest, ApiResponse, SelectionHistoryEntry }
